/** @file Shared text/visual masterdata search and variant selection for item and potential flows. */
import {
   Alert,
   Box,
   Button,
   Card,
   CardContent,
   Chip,
   CircularProgress,
   Dialog,
   DialogActions,
   DialogContent,
   DialogTitle,
   IconButton,
   Stack,
   TextField,
   Tooltip
} from "@mui/material";
import * as React from "react";
import {useEffect, useMemo, useState} from "react";
import SearchIcon from "@mui/icons-material/Search";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import CompareIcon from "@mui/icons-material/Compare";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {BodyText, TitleText} from "../../layout/Typography";
import {Thumbnail} from "../../components/Thumbnail";
import {UploadImage} from "../../components/UploadImage";
import {createUploadService} from "../../UploadService";
import {VariantCascade, VariantCascadeVariant} from "./VariantCascade";
import {
   MasterDataSearchProductFragment,
   useMasterDataSearchDialogSearchLazyQuery,
   useMasterDataSearchDialogUploadLazyQuery,
   useMasterDataSearchDialogVisualLazyQuery
} from "../../Queries";

const VISUAL_MAX_RESULTS = 24;

type MasterDataSearchDialogProps = {
   brandHouseId: string
   // Individual search terms (SKU, EAN, identifiers, review values, name) offered as clickable
   // badges above the search field. The first one is auto-searched when the dialog opens.
   seedSearches?: string[]
   // Resolved URLs of the BrandItem's own images. Used as the "reference" for compare and
   // as ready-to-use query images for visual search. Empty in create mode (no item yet).
   referenceImageUrls?: string[]
   // Ad-hoc images captured by an operator (BrandItem.vasImages). Shown in the reference panel
   // below the item's real images, clearly labelled as operator images.
   vasImageUrls?: string[]
   // The registered values of the item we are matching, shown as a reference card so the
   // operator can quickly pick the right style/colour/size. Omitted in create mode.
   current?: {name?: string | null, color?: string | null, size?: string | null}
   onCancel: () => void
   onPickVariant: (masterDataVariantId: string) => void
   onCreateEmpty?: () => void
   busy?: boolean
   errorMessage?: string
}

// A unified result row for both text and visual search.
type ResultHit = {
   product: MasterDataSearchProductFragment
   score?: number
}

const localized = (entries?: {locale: string, value: string}[] | null): string => {
   return entries?.[0]?.value || "";
}

const productThumb = (product: MasterDataSearchProductFragment): string | undefined => {
   return product.images?.find(image => !!image.thumbUrl)?.thumbUrl || undefined;
}

// All resolvable image URLs for a masterdata product, used to cycle through in the compare view.
const productImageUrls = (product: MasterDataSearchProductFragment): string[] => {
   return (product.images || [])
      .map(image => image.thumbUrl)
      .filter((url): url is string => !!url);
}

// Map a masterdata product's variants into the shape the VariantCascade picker expects.
// externalId is set to the variant _id so onPick yields the masterDataVariantId we attach.
const toCascadeVariants = (product: MasterDataSearchProductFragment): VariantCascadeVariant[] => {
   return product.variants.map(variant => ({
      id: variant._id,
      externalId: variant._id,
      sku: variant.sku,
      ean: variant.ean,
      options: Object.fromEntries((variant.options || []).map(option => [option.key, option.value])),
      imageUrl: variant.images?.find(image => !!image.thumbUrl)?.thumbUrl || null
   }));
}

/** Searches masterdata and lets callers act on a selected variant, with progress and error feedback. */
export const MasterDataSearchDialog = ({
                                          brandHouseId,
                                          seedSearches,
                                          referenceImageUrls,
                                          vasImageUrls,
                                          current,
                                          onCancel,
                                          onPickVariant,
                                          onCreateEmpty,
                                          busy,
                                          errorMessage
                                       }: MasterDataSearchDialogProps) => {
   // De-duplicated, non-empty seed terms rendered as clickable search badges.
   const seedTerms = useMemo(
      () => Array.from(new Set((seedSearches || []).map(term => term.trim()).filter(term => term.length > 0))),
      [seedSearches]
   );
   const [query, setQuery] = useState<string>(seedTerms[0] || "");
   const [activeTerm, setActiveTerm] = useState<string | undefined>(seedTerms[0]);
   const [mode, setMode] = useState<'text' | 'visual'>('text');
   const [expandedProductId, setExpandedProductId] = useState<string | undefined>(undefined);
   const [visualImageUrl, setVisualImageUrl] = useState<string | undefined>(undefined);
   const [compareIndex, setCompareIndex] = useState<number | null>(null);
   // Per-side image cursors for the compare dialog (left = clicked style, right = current item).
   const [leftImageIndex, setLeftImageIndex] = useState<number>(0);
   const [rightImageIndex, setRightImageIndex] = useState<number>(0);

   const uploadService = useMemo(
      () => createUploadService(import.meta.env.VITE_FILESERVICEURL!, brandHouseId),
      [brandHouseId]
   );

   const [runSearch, {data: searchData, loading: searchLoading, called: searchCalled}] = useMasterDataSearchDialogSearchLazyQuery({
      fetchPolicy: "no-cache"
   });
   const [runVisual, {data: visualData, loading: visualLoading, called: visualCalled}] = useMasterDataSearchDialogVisualLazyQuery({
      fetchPolicy: "no-cache"
   });
   const [resolveUpload] = useMasterDataSearchDialogUploadLazyQuery({fetchPolicy: "no-cache"});

   const searchText = (term: string) => {
      const trimmed = term.trim();
      if (trimmed.length === 0) {
         return;
      }
      setQuery(trimmed);
      setActiveTerm(trimmed);
      setMode('text');
      setExpandedProductId(undefined);
      runSearch({variables: {brandHouseId, search: trimmed}});
   }

   const submitText = () => searchText(query);

   const runVisualSearch = (imageUrl: string) => {
      setVisualImageUrl(imageUrl);
      setExpandedProductId(undefined);
      runVisual({variables: {brandHouseId, imageUrl, maxResults: VISUAL_MAX_RESULTS}});
   }

   // Seed an initial text search from the first identifier badge.
   useEffect(() => {
      if (seedTerms.length > 0) {
         runSearch({variables: {brandHouseId, search: seedTerms[0]}});
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   const resolveUploadedImage = async (imageId: string) => {
      const {data} = await resolveUpload({variables: {imageId}});
      if (data?.Image?.url) {
         runVisualSearch(data.Image.url);
      }
   }

   // Clipboard paste of an image -> upload -> visual search.
   useEffect(() => {
      if (mode !== 'visual') {
         return;
      }
      const onPaste = async (event: ClipboardEvent) => {
         const file = Array.from(event.clipboardData?.items || [])
            .find(item => item.type.startsWith("image/"))
            ?.getAsFile();
         if (!file) {
            return;
         }
         const response = await uploadService.upload(file, () => undefined);
         if (response?.success) {
            await resolveUploadedImage(response.imageId);
         }
      }
      window.addEventListener("paste", onPaste);
      return () => window.removeEventListener("paste", onPaste);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [mode, uploadService]);

   const hits: ResultHit[] = useMemo(() => {
      if (mode === 'visual') {
         return (visualData?.BrandHouseMasterDataVisualSearch || [])
            .filter((hit): hit is NonNullable<typeof hit> => !!hit?.product)
            .map(hit => ({product: hit.product!, score: hit.score}));
      }
      return (searchData?.BrandHouseMasterDataSearch || [])
         .filter((product): product is MasterDataSearchProductFragment => !!product)
         .map(product => ({product}));
   }, [mode, searchData, visualData]);

   const loading = mode === 'visual' ? visualLoading : searchLoading;
   const called = mode === 'visual' ? visualCalled : searchCalled;
   const referenceImageUrl = visualImageUrl || referenceImageUrls?.[0];
   const canCompare = !!referenceImageUrl;

   const onUploadImages = async (imageIds: string[] | string) => {
      const imageId = Array.isArray(imageIds) ? imageIds[0] : imageIds;
      if (imageId) {
         await resolveUploadedImage(imageId);
      }
   }

   // Open the compare dialog for a result, resetting both image cursors to the first image.
   const openCompare = (index: number) => {
      setLeftImageIndex(0);
      setRightImageIndex(0);
      setCompareIndex(index);
   }

   const renderVariantPicker = (product: MasterDataSearchProductFragment) => {
      if (product.variants.length === 0) {
         return <BodyText type={"body2"}>No variants on this style.</BodyText>;
      }
      return <Box sx={{marginTop: '8px'}}>
         <VariantCascade variants={toCascadeVariants(product)} onPick={onPickVariant}/>
      </Box>;
   }

   const renderResults = () => {
      if (mode === 'visual' && !visualImageUrl) {
         return renderVisualPicker();
      }
      if (loading) {
         return <Box sx={{textAlign: 'center', padding: '40px'}}><CircularProgress/></Box>;
      }
      if (called && hits.length === 0) {
         return <Alert severity={"info"} sx={{marginTop: '10px'}}>
            No matching styles found{mode === 'text' ? ' for this search.' : ' for this image.'}
         </Alert>;
      }
      return <Stack spacing={1} sx={{marginTop: '10px'}}>
         {hits.map((hit, index) => {
            const product = hit.product;
            const expanded = expandedProductId === product._id;
            return <Card key={product._id} variant={"outlined"}>
               <CardContent sx={{display: 'flex', gap: '12px', alignItems: 'center', cursor: 'pointer'}}
                            onClick={() => setExpandedProductId(expanded ? undefined : product._id)}>
                  <Thumbnail size={56} image={productThumb(product)}
                             onClick={canCompare && productThumb(product)
                                ? (event?: React.MouseEvent) => {
                                   event?.stopPropagation();
                                   openCompare(index);
                                }
                                : () => undefined}/>
                  <Box sx={{flex: 1}}>
                     <TitleText type={"h3"}>{localized(product.name)}</TitleText>
                     <BodyText type={"body2"}>
                        {product.style}
                        {hit.score !== undefined ? ` · match ${Math.round(hit.score)}` : ''}
                     </BodyText>
                  </Box>
                  {canCompare && productThumb(product) && (
                     <Tooltip title={"Compare with reference image"}>
                        <IconButton onClick={() => openCompare(index)}><CompareIcon/></IconButton>
                     </Tooltip>
                  )}
               </CardContent>
               {expanded && <CardContent sx={{paddingTop: 0}}>{renderVariantPicker(product)}</CardContent>}
            </Card>;
         })}
      </Stack>;
   }

   const renderVisualPicker = () => {
      return <Box sx={{marginTop: '10px'}}>
         <BodyText type={"body1"}>Pick an image to find similar styles — choose one of this item's images, upload, or paste from the clipboard.</BodyText>
         <Box sx={{display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '12px'}}>
            {(referenceImageUrls || []).map((url, index) => (
               <Box
                  key={index}
                  component={"img"}
                  src={url}
                  alt={""}
                  title={"Use this image for visual search"}
                  onClick={() => runVisualSearch(url)}
                  sx={{
                     width: '120px',
                     height: '120px',
                     objectFit: 'cover',
                     border: visualImageUrl === url ? '2px solid #1976d2' : '1px solid #d0d0d0',
                     borderRadius: '4px',
                     cursor: 'pointer'
                  }}
               />
            ))}
            <UploadImage id={"masterDataVisualUpload"} uploadService={uploadService} size={120} addImages={onUploadImages}/>
         </Box>
      </Box>;
   }

   // One side of the compare view: a large image with prev/next arrows when it has multiple images.
   const renderCompareSide = (title: string, images: string[], index: number, setIndex: (next: number) => void) => {
      const clamped = Math.min(index, Math.max(0, images.length - 1));
      return <Box sx={{flex: 1, textAlign: 'center'}}>
         <BodyText type={"body2"}>{title}</BodyText>
         <Thumbnail size={320} image={images[clamped]}/>
         {images.length > 1 && (
            <Stack direction={"row"} spacing={1} justifyContent={"center"} alignItems={"center"}>
               <IconButton size={"small"} disabled={clamped === 0} onClick={() => setIndex(clamped - 1)}><ChevronLeftIcon/></IconButton>
               <BodyText type={"body2"}>{clamped + 1} / {images.length}</BodyText>
               <IconButton size={"small"} disabled={clamped === images.length - 1} onClick={() => setIndex(clamped + 1)}><ChevronRightIcon/></IconButton>
            </Stack>
         )}
      </Box>;
   }

   const renderCompare = () => {
      if (compareIndex === null) {
         return null;
      }
      const hit = hits[compareIndex];
      if (!hit) {
         return null;
      }
      const clickedImages = productImageUrls(hit.product);
      const currentItemImages = (referenceImageUrls && referenceImageUrls.length > 0)
         ? referenceImageUrls
         : (visualImageUrl ? [visualImageUrl] : []);
      // Move to the previous/next result, resetting the clicked-side cursor to its first image.
      const stepResult = (delta: number) => {
         const next = compareIndex + delta;
         if (next >= 0 && next < hits.length) {
            setLeftImageIndex(0);
            setCompareIndex(next);
         }
      }
      return <Dialog open={true} onClose={() => setCompareIndex(null)} fullWidth maxWidth={"md"}>
         <DialogTitle sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <span>Compare with reference</span>
            <IconButton onClick={() => setCompareIndex(null)}><CloseIcon/></IconButton>
         </DialogTitle>
         <DialogContent>
            <Stack direction={"row"} spacing={2} alignItems={"flex-start"}>
               {renderCompareSide(localized(hit.product.name) || "Selected style", clickedImages, leftImageIndex, setLeftImageIndex)}
               {renderCompareSide("Current item", currentItemImages, rightImageIndex, setRightImageIndex)}
            </Stack>
         </DialogContent>
         <DialogActions>
            <IconButton disabled={compareIndex === 0} onClick={() => stepResult(-1)}><ChevronLeftIcon/></IconButton>
            <BodyText type={"body2"}>{compareIndex + 1} / {hits.length}</BodyText>
            <IconButton disabled={compareIndex === hits.length - 1} onClick={() => stepResult(1)}><ChevronRightIcon/></IconButton>
            <Box sx={{flex: 1}}/>
            <Button variant={"contained"} onClick={() => {
               setExpandedProductId(hit.product._id);
               setCompareIndex(null);
            }}>Select this style</Button>
         </DialogActions>
      </Dialog>;
   }

   const hasReference = !!(current?.name || current?.color || current?.size || referenceImageUrls?.length || vasImageUrls?.length);

   const renderReferencePanel = () => {
      return <Box sx={{width: '220px', flexShrink: 0, borderLeft: '1px solid #e0e0e0', paddingLeft: '16px'}}>
         <TitleText type={"h3"}>Current item</TitleText>
         {(referenceImageUrls || []).length > 0 && (
            <Stack spacing={1} sx={{marginTop: '8px'}}>
               {(referenceImageUrls || []).map((url, index) => (
                  <Box key={index} component={"img"} src={url} alt={""}
                       sx={{width: '100%', height: 'auto', objectFit: 'contain', border: '1px solid #d0d0d0', borderRadius: '4px'}}/>
               ))}
            </Stack>
         )}
         {(vasImageUrls || []).length > 0 && (
            <Box sx={{marginTop: '12px'}}>
               <BodyText type={"body2"} sx={{color: '#b26a00'}}>Ad-hoc operator images</BodyText>
               <Stack spacing={1} sx={{marginTop: '4px'}}>
                  {(vasImageUrls || []).map((url, index) => (
                     <Box key={index} component={"img"} src={url} alt={""}
                          sx={{width: '100%', height: 'auto', objectFit: 'contain', border: '1px dashed #e0a030', borderRadius: '4px'}}/>
                  ))}
               </Stack>
            </Box>
         )}
         <Stack spacing={0.5} sx={{marginTop: '12px'}}>
            {[{label: 'Name', value: current?.name}, {label: 'Color', value: current?.color}, {label: 'Size', value: current?.size}]
               .filter(row => !!row.value)
               .map(row => (
                  <Box key={row.label}>
                     <BodyText type={"body2"} sx={{color: '#909090'}}>{row.label}</BodyText>
                     <BodyText type={"body1"}>{row.value}</BodyText>
                  </Box>
               ))}
         </Stack>
      </Box>;
   }

   return <>
      <Dialog open={true} onClose={onCancel} fullWidth maxWidth={"lg"}>
         <DialogTitle>
            <Stack spacing={1}>
               {seedTerms.length > 0 && (
                  <Box sx={{display: 'flex', flexWrap: 'wrap', gap: '6px'}}>
                     {seedTerms.map(term => {
                        const active = mode === 'text' && activeTerm === term;
                        return <Chip
                           key={term}
                           label={term}
                           size={"small"}
                           color={active ? "primary" : "default"}
                           variant={active ? "filled" : "outlined"}
                           onClick={() => searchText(term)}
                        />;
                     })}
                  </Box>
               )}
               <Stack direction={"row"} spacing={1} alignItems={"center"}>
                  <TextField
                     fullWidth
                     size={"small"}
                     autoFocus
                     value={query}
                     placeholder={"Search masterdata by SKU, EAN or name…"}
                     onChange={event => setQuery(event.target.value)}
                     onKeyDown={event => {
                        if (event.key === 'Enter') {
                           event.preventDefault();
                           submitText();
                        }
                     }}
                  />
                  <Button variant={"contained"} startIcon={<SearchIcon/>} onClick={submitText}
                          disabled={query.trim().length === 0}>Search</Button>
                  <Button variant={mode === 'visual' ? "contained" : "outlined"} startIcon={<ImageSearchIcon/>}
                          onClick={() => setMode('visual')}>Visual</Button>
               </Stack>
            </Stack>
         </DialogTitle>
         <DialogContent sx={{minHeight: '500px'}}>
            {errorMessage && <Alert severity={"error"} sx={{marginBottom: '12px'}}>{errorMessage}</Alert>}
            <Stack direction={"row"} spacing={2} sx={{height: '100%'}}>
               <Box sx={{flex: 1, minWidth: 0}}>
                  {busy && <Box sx={{textAlign: 'center', padding: '40px'}}><CircularProgress/></Box>}
                  {/* Keep the variant picker mounted so a failed action cannot auto-select it again. */}
                  <Box hidden={busy}>{renderResults()}</Box>
               </Box>
               {hasReference && renderReferencePanel()}
            </Stack>
         </DialogContent>
         <DialogActions>
            {onCreateEmpty && <Button variant={"contained"} onClick={onCreateEmpty}>Create empty</Button>}
            <Button variant={"outlined"} onClick={onCancel}>Cancel</Button>
         </DialogActions>
      </Dialog>
      {renderCompare()}
   </>;
}
